import {DOCUMENT} from '@angular/common';
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNumberOfCreelPositionsData} from '@application/helper/navigation-helper/navigation-number-of-creel-positions-data.interface';
import {NavigationSelectedMenuItemData} from '@application/helper/navigation-helper/navigation-selected-menu-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {StringUtils} from '@application/helper/string-utils';
import {UnsavedChangesNotifierService} from '@application/helper/unsaved-changes-notifier/unsaved-changes-notifier.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {ConflictType} from '@domain/conflicts/conflict-type';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {ColorSet} from '@domain/textile-data/color-set/color-set';
import {HorizontalPositionOfGroupCreelPosition} from '@domain/textile-data/colored-yarn-set/horizontal-position-of-group-creel-position';
import {CreelPosition} from '@domain/textile-data/creel/creel-position';
import {HorizontalPositionOfCreelPosition} from '@domain/textile-data/creel/horizontal-position-of-creel-position';
import {MaterialSet} from '@domain/textile-data/textile-data';
import {YarnSet} from '@domain/textile-data/yarn-set/yarn-set';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {COLOR_SETS, ColorSets} from '@infrastructure/http/color-set/color-sets';
import {AddColorSetPageComponent} from '@presentation/pages/textile-data/color-set/add/add-color-set-page.component';
import {SelectColorComponent} from '@presentation/pages/textile-data/material-set/add/select-color/select-color.component';
import {SelectYarnTypeComponent} from '@presentation/pages/textile-data/material-set/add/select-yarn-type/select-yarn-type.component';
import {AddYarnSetPageComponent} from '@presentation/pages/textile-data/yarn-set/add/add-yarn-set-page.component';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  ColDefBuilderFactoryService,
  Conflict,
  ConflictsDialogComponent,
  ConflictsDialogData,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  GridOptionsBuilderFactoryService,
  MobileColDef,
  NoDataOverlayComponentParams,
  ObjectUtils,
  SaveType,
  skeletonViewAnimation,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {GridApi, GridOptions, GridReadyEvent, ICellRendererParams, ITooltipParams, RowNode, RowSelectedEvent, ValueGetterParams} from 'ag-grid-community';
import {clone, cloneDeep, filter, find, findIndex, forEach, indexOf, isEmpty, isEqual, isNil, isUndefined, remove, size, some, times} from 'lodash-es';
import {map, ReplaySubject} from 'rxjs';
import {finalize, switchMap, takeUntil} from 'rxjs/operators';
import {AddColorSetViewMode} from '../../color-set/add/add-color-set-view-mode';
import {OverviewListColorSet} from '../../color-set/overview/overview-list-color-set';
import {GridColoredYarnSetPreviewComponent} from '../../colored-yarn-set/overview/grid-colored-yarn-set-preview/grid-colored-yarn-set-preview.component';
import {TextileService} from '../../textile-data-overview/textile.service';
import {TextileDataType} from '../../textile-data-type.enum';
import {AdditionalOptionsForAddColorSetForm, GeneralForm, MaterialSetForm} from './material-set-form';
import {NavigationAddMaterialSetPageData} from './navigation-add-material-set-page-data.type';
import {NavigationMaterialSetData} from './navigation-material-set-data.interface';

@Component({
  selector: 'app-add-material-set-page',
  templateUrl: './add-material-set-page.component.html',
  styleUrls: ['./add-material-set-page.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .mat-card-header, .button-skeleton-wrapper, .title-skeleton-wrapper, .list-skeleton-wrapper')]
})
export class AddMaterialSetPageComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public materialSet: MaterialSet;
  @Input() public hasMaterialSetState = false;
  @Input() public textileDataService;
  @ViewChild('selectColorComponent') public selectColorComponent: SelectColorComponent;
  @ViewChild('selectYarnTypeComponent') public selectYarnTypeComponent: SelectYarnTypeComponent;

  public addMaterialSetForm: MaterialSetForm;
  public permissionToEditMaterialSet: Permission;
  public selectedMenuItem: TabsMenuItem;
  public gridOptionsForColorSets: GridOptions;
  public gridModelForListOfColorSets: GridApi;
  public listOfColorSets: OverviewListColorSet[];
  public suggestedMaterialSetName: string;

  public generalMenuItem: TabsMenuItem = {
    value: AddColorSetViewMode.GENERAL,
    translationKey: 'GENERAL.GENERAL'
  };

  public optionalSettingsMenuItem: TabsMenuItem = {
    value: AddColorSetViewMode.OPTIONAL_SETTINGS,
    translationKey: 'GENERAL.OPTIONAL_SETTINGS'
  };

  public alternativeSetsMenuItem: TabsMenuItem = {
    value: AddColorSetViewMode.ALTERNATIVE_SETS,
    translationKey: 'TEXTILE_DATA.COLOR_SET.ALTERNATIVE_SETS'
  };

  public menuItems: TabsMenuItem[] = [];
  public headerIdentifier = HeaderIdentifier.ADD_YARN_SET;
  public readonly SAVE_TYPE = SaveType;

  private urlParams: Params;
  private modifiedCreelPositions: number[] = [];
  private readonly formValidationHelper: FormValidationHelper = new FormValidationHelper();
  private readonly possibleCreelPositionsSubject = new ReplaySubject<OverviewListColorSet[]>();
  private saveButtonTouched = false;
  private currentSubscription: Subscription;
  private slideToggleChecked = false;
  private selectedColorSets: number[];
  private initialListOfColorSets: OverviewListColorSet[];

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(COLOR_SETS) public readonly colorSets: ColorSets,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly colDefBuilderFactory: ColDefBuilderFactoryService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly navigationHelperService: NavigationHelperService<NavigationAddMaterialSetPageData>,
    private readonly textileService: TextileService,
    private unsavedChangesNotifierService: UnsavedChangesNotifierService
  ) {
    super();
  }

  public get generalForm(): GeneralForm {
    return this.addMaterialSetForm?.controls?.generalForm;
  }

  public get additionalOptionsForAddColorSetForm(): AdditionalOptionsForAddColorSetForm {
    return this.addMaterialSetForm.controls.additionalOptionsForAddColorSetForm;
  }

  public ngOnInit(): void {
    this.getEmitValue();
    this.urlParams = this.activatedRoute.snapshot.params;
    this.permissionToEditMaterialSet = LastModifiedCardUtils.getPermissionToModifyItems(this.isMaterialSetOfTypeColorSet() ? 'colorSet' : 'yarnSet');
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.initializeSelectedMenuItem();
    this.initializeGridOptionsForColorSets();
    this.setForm();
    this.setFormFields();
    this.unsavedChangesNotifierService.getPartialStateOfUnsavedChangesNotifier();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('materialSet' in changes && !changes.materialSet.isFirstChange()) {
      this.generalForm.controls.name.setAsyncValidators(AsyncUniqueValidator.createValidator(this.textileDataService, this.hasEditedMaterialSet() ? this.materialSet.name : null));
      this.setFormFields();
    }
  }

  public onNavigationHelperDestroy(): void {
    this.updateMaterialSet();
    this.navigationHelperService.savePartialState<NavigationMaterialSetData>({materialSet: this.materialSet});
    this.navigationHelperService.savePartialState<NavigationSelectedMenuItemData>({menuItem: this.selectedMenuItem});
    this.unsavedChangesNotifierService.savePartialStateOfUnsavedChangesNotifier();
  }

  public canShowSkeletonView(): boolean {
    return AssertionUtils.isNullOrUndefined(this.materialSet);
  }

  public canShowGeneralForm(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.generalForm);
  }

  public canShowIllegalMaterialSetReason(): boolean {
    return this.hasCreelPositions() && (this.hasEmptyCreelPosition() || this.containsGroupsWithNoOrOneMaterial());
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(LastModifiedCardUtils.getPageUrls(this.getMaterialType()).overview);
  }

  public canSaveMaterialSet(): boolean {
    return !this.canShowSkeletonView() && this.canShowGeneralForm() && this.generalForm.valid && this.hasCreelPositions() && !this.hasEmptyCreelPosition() && !this.containsGroupsWithNoOrOneMaterial();
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid =
      (!isNil(this.generalForm) ? some(this.generalForm.controls, (control: FormControl) => control.invalid && control.touched) : false) ||
      !this.hasCreelPositions() ||
      this.canShowIllegalMaterialSetReason();
    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }
    return isFormInvalid && this.saveButtonTouched;
  }

  public saveMaterialSet(saveType: SaveType, navigateToSelectedRoute?: string): void {
    this.saveButtonTouched = true;
    this.formValidationHelper.checkForm(this.generalForm, this.document);
    this.modifiedCreelPositions = times(this.materialSet.getSizeOfCreelPositions(), (index: number) => index);
    this.saveButtonTouched = true;
    if (this.canSaveMaterialSet()) {
      this.updateMaterialSet();
      this.saving = true;

      const request = this.isEditingMaterialSet() ? this.textileDataService.update(this.materialSet) : this.textileDataService.save(this.materialSet);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving())).subscribe({
        next: (id: number) => {
          this.unsavedChangesNotifierService.notifyChangesToForm(false);
          this.textileService.navigateAndShowToast(
            saveType,
            this.getTextileDataType(),
            this.getTranslationKeyForMaterialSet(),
            this.isEditingMaterialSet(),
            this.materialSet.name,
            id,
            navigateToSelectedRoute
          );
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingMaterialSet() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public deleteMeterialSet(): void {
    this.textileService.removeConfirmation(this.materialSet, this.getTextileDataType(), false, null, this.textileDataService);
  }

  public duplicateMeterialSet(): void {
    const duplicateUrl = this.isMaterialSetOfTypeColorSet() ? RouteUtils.paths.texStyle.colorSet.duplicateColorSet.absolutePath : RouteUtils.paths.texStyle.yarnSet.duplicateYarnSet.absolutePath;
    this.router.navigateByUrl(duplicateUrl.replace(':id', this.materialSet.id.toString()));
  }

  public hasCreelPositions(): boolean {
    return isNil(this.materialSet) ? false : !isEqual(this.materialSet.getSizeOfCreelPositions(), 0);
  }

  public canAddCreelPositions(): boolean {
    return this.canShowGeneralForm() && this.generalForm.controls.creelPositions.valid;
  }

  public addCreelPositions(): void {
    const numberOfCreelPositions = this.generalForm.value.creelPositions;
    this.materialSet.creelPositions = times(numberOfCreelPositions, () => new CreelPosition([], false));
    this.materialSet.creelPositions.reverse();

    if (this.isMaterialSetOfTypeColorSet()) {
      this.getSuggestionForMaterialSetName();
      this.listOfColorSets = this.initialListOfColorSets.filter((colorSet: OverviewListColorSet) => colorSet.creelPositions.length === numberOfCreelPositions);
      this.possibleCreelPositionsSubject.next(this.listOfColorSets);
    }
  }

  public getSelectedCreelPositions(): CreelPosition[] {
    return filter(this.materialSet?.creelPositions, 'isSelected');
  }

  public canShowCreelPositionDetailsAndMaterialTypeSelection(): boolean {
    return this.canShowGeneralForm() && this.hasCreelPositions() && this.hasSelectedCreelPosition();
  }

  public hasSelectedCreelPosition(): boolean {
    return !isEmpty(this.getSelectedCreelPositions());
  }

  public canShowCreelPositionDetails(): boolean {
    return isEqual(size(this.getSelectedCreelPositions()), 1);
  }

  public getSelectedCreelPosition(): CreelPosition {
    return find(this.materialSet.creelPositions, 'isSelected');
  }

  public selectAllCreelPositions(event: MatCheckboxChange): void {
    for (const creelPosition of this.materialSet.creelPositions) {
      creelPosition.isSelected = event.checked;
    }
  }

  public creelPositionHasBeenChanged(): void {
    this.materialSet = clone(this.materialSet);
    const creelPositionIndex = this.getSelectedCreelPositionIndex();

    if (!this.canShowErrorForCreelPositionIndex(creelPositionIndex)) {
      setTimeout(() => {
        this.modifiedCreelPositions.push(creelPositionIndex);
      });
    }

    if (this.isMaterialSetOfTypeYarnSet()) {
      this.getSuggestionForMaterialSetName();
    }
    this.unsavedChangesNotifierService.notifyChangesToOthers();
  }

  public isMaterialSetOfTypeColorSet(): boolean {
    return this.materialSet && this.materialSet instanceof ColorSet;
  }

  public isMaterialSetOfTypeYarnSet(): boolean {
    return this.materialSet && this.materialSet instanceof YarnSet;
  }

  public canShowSelectColor(): boolean {
    return this.isMaterialSetOfTypeColorSet() && !this.isMaterialSetBeingUsed();
  }

  public canShowSelectYarnType(): boolean {
    return this.isMaterialSetOfTypeYarnSet() && !this.isMaterialSetBeingUsed();
  }

  public isEditingMaterialSet(): boolean {
    const path = this.activatedRoute.routeConfig.path;

    return (
      !isNil(this.activatedRoute.snapshot.routeConfig) &&
      (isEqual(path, AddColorSetPageComponent.urlToEditColorSet) || isEqual(path, AddYarnSetPageComponent.urlToEditYarnSet)) &&
      !isUndefined(this.urlParams.id)
    );
  }

  public isMaterialSetSelected(): boolean {
    return !this.canShowSkeletonView() && this.hasCreelPositions() && this.areAllCreelPositionsSelected();
  }

  public canSelectCreelPosition(): boolean {
    return this.canShowSkeletonView() || (this.hasCreelPositions() && this.canEditMaterialSet());
  }

  public canShowListOfColorSets(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.listOfColorSets);
  }

  public canShowCreelPositionsInput(): boolean {
    return !this.canShowSkeletonView() && !this.hasCreelPositions();
  }

  public canShowErrorForCreelPositionIndex(creelPositionIndex: number): boolean {
    return indexOf(this.modifiedCreelPositions, creelPositionIndex) >= 0;
  }

  public selectedCreelPositionChanged(): void {
    if (this.canShowCreelPositionDetails() && this.canEditMaterialSet()) {
      if (this.isMaterialSetOfTypeColorSet()) {
        this.selectColorComponent.filterForListOfColors.focus();
      } else if (this.isMaterialSetOfTypeYarnSet()) {
        this.selectYarnTypeComponent.filterComponent.focus();
      }
    }
  }

  public canShowErrorForSelectedCreelPosition(): boolean {
    return this.canShowErrorForCreelPositionIndex(this.getSelectedCreelPositionIndex());
  }

  public showInUseUnableToSave(): boolean {
    return this.isMaterialSetOfTypeColorSet() ? false : this.isMaterialSetBeingUsed();
  }

  public isMaterialSetBeingUsed(): boolean {
    return !isUndefined(this.materialSet) && !!this.materialSet.used && !this.isDuplicatingMaterialSet();
  }

  public canEditMaterialSet(): boolean {
    return this.hasPermissionToEditMaterialSet() && !this.isMaterialSetBeingUsed();
  }

  public hasPermissionToEditMaterialSet(): boolean {
    return this.currentSubscription?.hasPermission(this.permissionToEditMaterialSet);
  }

  public openConflictsDialog(): void {
    this.textileDataService
      .getConflicts(this.materialSet.id)
      .pipe(
        switchMap((conflicts: Conflict[]) => {
          return this.dialogBuilderFactoryService
            .getBuilder()
            .withClass('alert-dialog')
            .openDialog(ConflictsDialogComponent, ConflictsDialogData.createInUseData(this.getTranslationKeyForMaterialSet(), this.materialSet.name, conflicts, ConflictType));
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe();
  }

  public showSelected(slideToggleChange: MatSlideToggleChange): void {
    this.slideToggleChecked = slideToggleChange.checked;

    if (slideToggleChange.checked) {
      this.selectedColorSets = this.gridModelForListOfColorSets.getSelectedNodes().map((selectedNode: RowNode) => selectedNode.data.id);
      this.listOfColorSets = this.gridModelForListOfColorSets.getSelectedNodes().map((node: RowNode) => node.data);
    } else {
      this.listOfColorSets = cloneDeep(this.initialListOfColorSets.filter((colorSet: OverviewListColorSet) => colorSet.creelPositions.length === this.materialSet.getSizeOfCreelPositions()));
    }
  }

  public onMaterialSetNameChange(): void {
    this.materialSet.name = this.generalForm.value.name;
  }

  public getTabAction(): string {
    return this.isEditingMaterialSet()
      ? this.translate.instant('GENERAL.ACTIONS.EDIT_OBJECT', {object: this.translate.instant(this.getTranslationKeyForMaterialSet(), {count: 1}).toLowerCase()})
      : this.translate.instant('GENERAL.ACTIONS.CREATE_OBJECT', {object: this.translate.instant(this.getTranslationKeyForMaterialSet(), {count: 1}).toLowerCase()});
  }

  public onSelectedMenuItemChange(menuItem: TabsMenuItem): void {
    this.selectedMenuItem = menuItem;
  }

  public showOptionalSettingsTab(): boolean {
    return this.isMaterialSetOfTypeColorSet() && isEqual(this.selectedMenuItem.value, AddColorSetViewMode.OPTIONAL_SETTINGS);
  }

  public showAlternativeSetsTab(): boolean {
    return this.selectedMenuItem.value === AddColorSetViewMode.ALTERNATIVE_SETS;
  }

  public showSelectionCard(): boolean {
    return !this.showOptionalSettingsTab() && !this.showAlternativeSetsTab() && this.hasPermissionToEditMaterialSet();
  }

  public getLoadLink(): string {
    this.unsavedChangesNotifierService.notifyChangesToForm(false);
    return ErrorHandlers.getLoadLink(LastModifiedCardUtils.getPageUrls(this.getMaterialType()).edit, this.generalForm.controls['name']);
  }

  public canShowSuggestionForMaterialSetName(): boolean {
    return this.suggestedMaterialSetName?.length > 0 && this.generalForm.value.name !== this.suggestedMaterialSetName;
  }

  public applySuggestionForMaterialSetName(): void {
    this.generalForm.controls.name.setValue(this.suggestedMaterialSetName);
  }

  public navigateToCustomSettings(): void {
    this.router.navigateByUrl(this.isMaterialSetOfTypeColorSet() ? RouteUtils.paths.texStyle.colorSet.colorSetSettings.absolutePath : RouteUtils.paths.texStyle.yarnSet.yarnSetSettings.absolutePath);
  }

  private getEmitValue(): void {
    this.unsavedChangesNotifierService
      .getSaveEventEmitter()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((res: {action: boolean; navigateToSelectedRoute: string}) => {
        if (res) {
          this.saveMaterialSet(SaveType.SAVE_AND_CLOSE, res.navigateToSelectedRoute);
        }
      });
  }

  private initializeSelectedMenuItem(): void {
    this.selectedMenuItem = this.generalMenuItem;

    const emptySelectedMenuItemState = {menuItem: null} as NavigationSelectedMenuItemData;
    const selectedMenuItemState = this.navigationHelperService.getPartialState<NavigationSelectedMenuItemData>(Object.keys(emptySelectedMenuItemState));

    if (!AssertionUtils.isNullOrUndefined(selectedMenuItemState) && !ObjectUtils.isDeepEqual(selectedMenuItemState, emptySelectedMenuItemState)) {
      this.selectedMenuItem = selectedMenuItemState.menuItem;
    }
  }

  private getSelectedCreelPositionIndex(): number {
    return findIndex(this.materialSet.creelPositions, this.getSelectedCreelPosition());
  }

  private getTextileDataType(): TextileDataType {
    return this.isMaterialSetOfTypeColorSet() ? TextileDataType.COLOR_SET : TextileDataType.YARN_SET;
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.getTranslationKeyForMaterialSet(), {
          count: 1
        })
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private setForm(): void {
    const generalForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required, AsyncUniqueValidator.createValidator(this.textileDataService, this.hasEditedMaterialSet() ? this.materialSet.name : null)),
      creelPositions: this.formBuilder.control(null, [Validators.required, Validators.min(1), Validators.max(32)])
    });

    this.addMaterialSetForm = this.formBuilder.group({
      generalForm
    });

    this.generalMenuItem.form = this.generalForm;
    this.unsavedChangesNotifierService.notifyChanges(this.generalForm, this.unSubscribeOnViewDestroy);
  }

  private setFormFields(): void {
    this.generalForm.setValue({
      name: this.hasEditedMaterialSet() ? this.materialSet.name : null,
      creelPositions: !AssertionUtils.isNullOrUndefined(this.materialSet?.creelPositions) ? this.materialSet.getSizeOfCreelPositions() : null
    });

    if (this.isMaterialSetOfTypeColorSet()) {
      this.headerIdentifier = HeaderIdentifier.ADD_COLOR_SET;
      this.menuItems = [this.generalMenuItem, this.optionalSettingsMenuItem, this.alternativeSetsMenuItem];

      this.addMaterialSetForm.controls.additionalOptionsForAddColorSetForm = this.formBuilder.group({
        technicalCode: this.formBuilder.control(this.hasEditedMaterialSet() ? this.materialSet.technicalCode : null)
      });
      this.unsavedChangesNotifierService.notifyChanges(this.additionalOptionsForAddColorSetForm, this.unSubscribeOnViewDestroy);
      this.selectedColorSets = this.materialSet.alternativeColorSetIds;
      this.getColorSets();
    }

    const emptyNumberOfCreelPositionsState = {numberOfCreelPositions: null} as NavigationNumberOfCreelPositionsData;
    const numberOfCreelPositionsState = this.navigationHelperService.getPartialState<NavigationNumberOfCreelPositionsData>(Object.keys(emptyNumberOfCreelPositionsState), true);

    if (!AssertionUtils.isNullOrUndefined(numberOfCreelPositionsState) && !ObjectUtils.isDeepEqual(numberOfCreelPositionsState, emptyNumberOfCreelPositionsState)) {
      this.generalForm.controls.creelPositions.setValue(numberOfCreelPositionsState.numberOfCreelPositions);
      this.addCreelPositions();
    }

    this.getSuggestionForMaterialSetName();
  }

  private hasEditedMaterialSet(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.materialSet) && (this.isEditingMaterialSet() || this.hasMaterialSetState);
  }

  private initializeGridOptionsForColorSets(): void {
    this.gridOptionsForColorSets = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfColoredYarnSets(), GridIdentifier.ALTERNATIVE_COLORED_YARN_SETS)
      .withRowSelection(true)
      .withOnRowSelected((params: RowSelectedEvent) => this.rowSelected(params))
      .withOnGridReady((params: GridReadyEvent) => this.onGridReadyEventForListOfColoredYarnSets(params))
      .withOnRowDataUpdated(() => this.selectColorSets())
      .withNoRowsOverlay({
        isAnyFilterPresent: () => true
      } as NoDataOverlayComponentParams)
      .withRememberStateForNavigationHelper()
      .build();
  }

  private getColumnDefsForListOfColoredYarnSets(): MobileColDef[] {
    return [
      this.colDefBuilderFactory.getBuilder().withField('name', true).withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withHeaderName('TEXTILE_DATA.MATERIAL_SET.NUMBER_OF_CREEL_POSITIONS')
        .withCellRenderer((params: ICellRendererParams) => `${size((params.data as OverviewListColorSet).creelPositions)}`)
        .withTooltipValueGetter((params: ITooltipParams) => `${size((params.data as OverviewListColorSet).creelPositions)}`)
        .withNumericMultiFilter(
          this.possibleCreelPositionsSubject.asObservable().pipe(
            map((listOfColorSet: OverviewListColorSet[]) => this.getPossibleCreelPositions(listOfColorSet)),
            takeUntil(this.unSubscribeOnViewDestroy)
          )
        )
        .withFilterValueGetter((params: ValueGetterParams) => params.data.creelPositions.length)
        .withSortable(false)
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('color')
        .withHeaderName('TEXTILE_DATA.COLOR.COLOR')
        .withTooltipValueGetter((params: ITooltipParams) => StringUtils.createStringFromColorsOrYarnTypes(params.data.getUniqueColors()))
        .withCellRenderer((params: ICellRendererParams) => StringUtils.createStringFromColorsOrYarnTypes(params.data.getUniqueColors()))
        .withComparator((valueA: any, valueB: any, nodeA: RowNode, nodeB: RowNode) => {
          const firstColorSetOverviewList: OverviewListColorSet = nodeA.data;
          const colorNamesOfFirstColorSet: string = StringUtils.createStringFromColorsOrYarnTypes(firstColorSetOverviewList.getUniqueColors());

          const secondColorSetOverviewList: OverviewListColorSet = nodeB.data;
          const colorNamesOfSecondColorSet: string = StringUtils.createStringFromColorsOrYarnTypes(secondColorSetOverviewList.getUniqueColors());

          return StringUtils.stringComparator(colorNamesOfFirstColorSet, colorNamesOfSecondColorSet);
        })
        .withoutFilter()
        .withSortable()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withValueGetter((params: ValueGetterParams<any>) => params.data)
        .withHeaderName('GENERAL.PREVIEW')
        .withCellRenderer(GridColoredYarnSetPreviewComponent)
        .withoutFilter()
        .build()
    ];
  }

  private isDuplicatingMaterialSet(): boolean {
    const path = this.activatedRoute.routeConfig.path;

    return (
      !isNil(this.activatedRoute.snapshot.routeConfig) &&
      (isEqual(path, AddColorSetPageComponent.urlToDuplicateColorSet) || isEqual(path, AddYarnSetPageComponent.urlToDuplicateYarnSet)) &&
      !isUndefined(this.urlParams.id)
    );
  }

  private hasEmptyCreelPosition(): boolean {
    let result = false;

    forEach(this.materialSet.creelPositions, (creelPosition: CreelPosition, index: number) => {
      if (this.canShowErrorForCreelPositionIndex(index) && isEmpty(creelPosition.horizontalPositionsOfCreelPosition)) {
        result = true;
        return false;
      }
    });

    return result;
  }

  private containsGroupsWithNoOrOneMaterial(): boolean {
    let result = false;

    forEach(this.materialSet.creelPositions, (creelPosition: CreelPosition, index: number) => {
      if (this.canShowErrorForCreelPositionIndex(index) && this.containsGroupWithNoOrOneMaterial(creelPosition.horizontalPositionsOfCreelPosition)) {
        result = true;
        return false;
      }
    });

    return result;
  }

  private selectColorSets(): void {
    if (!AssertionUtils.isNullOrUndefined(this.gridModelForListOfColorSets)) {
      this.selectedColorSets?.forEach((selectedColoredYarnSet: number) => {
        const node = this.gridModelForListOfColorSets.getRowNode(`${selectedColoredYarnSet}`);

        if (!AssertionUtils.isNullOrUndefined(node)) {
          node.setSelected(true);
        }
      });
    }
  }

  private getColorSets(): void {
    this.colorSets
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfColorSets: OverviewListColorSet[]) => {
        remove(listOfColorSets, (colorSet: OverviewListColorSet) => this.materialSet.id === colorSet.id);
        this.initialListOfColorSets = cloneDeep(listOfColorSets);
        this.listOfColorSets = listOfColorSets.filter((colorSet: OverviewListColorSet) => colorSet.creelPositions.length === this.materialSet.getSizeOfCreelPositions());
        this.possibleCreelPositionsSubject.next(this.listOfColorSets);
      });
  }

  private rowSelected(params: RowSelectedEvent): void {
    if (!params.node.isSelected() && this.slideToggleChecked) {
      remove(this.listOfColorSets, (coloredYarnSet: OverviewListColorSet) => isEqual(coloredYarnSet.id, params.data.id));
      this.listOfColorSets = cloneDeep(this.listOfColorSets);
      this.changeDetectorRef.detectChanges();
    }
    this.selectedColorSets = this.gridModelForListOfColorSets.getSelectedNodes().map((selectedNode: RowNode) => selectedNode.data.id);
    this.unsavedChangesNotifierService.notifyChangesToOthers();
  }

  private onGridReadyEventForListOfColoredYarnSets(params: GridReadyEvent): void {
    this.gridModelForListOfColorSets = params.api;

    this.selectColorSets();
  }

  private containsGroupWithNoOrOneMaterial(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): boolean {
    let result = false;

    for (const horizontalPositionOfCreelPosition of horizontalPositionsOfCreelPosition) {
      if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionsOfGroupCreelPosition: HorizontalPositionOfCreelPosition[] = (horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition).group;

        if (size(horizontalPositionsOfGroupCreelPosition) <= 1 || this.containsGroupWithNoOrOneMaterial(horizontalPositionsOfGroupCreelPosition)) {
          result = true;
          break;
        }
      }
    }

    return result;
  }

  private getMaterialType(): string {
    let result: string;

    if (this.isMaterialSetOfTypeYarnSet()) {
      result = 'yarnSet';
    } else if (this.isMaterialSetOfTypeColorSet()) {
      result = 'colorSet';
    }

    return result;
  }

  private areAllCreelPositionsSelected(): boolean {
    return isEqual(size(this.materialSet.creelPositions), size(this.getSelectedCreelPositions()));
  }

  private getTranslationKeyForMaterialSet(): string {
    return this.isMaterialSetOfTypeColorSet() ? 'TEXTILE_DATA.COLOR_SET.COLOR_SET' : 'TEXTILE_DATA.YARN_SET.YARN_SET';
  }

  private getPossibleCreelPositions(listOfColorSet: OverviewListColorSet[]): string[] {
    return listOfColorSet.map((colorSet: OverviewListColorSet) => colorSet.creelPositions.length.toString());
  }

  private updateMaterialSet(): void {
    if (!AssertionUtils.isNullOrUndefined(this.materialSet)) {
      this.materialSet.name = this.generalForm.value.name;
      if (this.isMaterialSetOfTypeColorSet()) {
        this.materialSet.alternativeColorSetIds = !this.showAlternativeSetsTab()
          ? this.selectedColorSets
          : this.gridModelForListOfColorSets.getSelectedNodes()?.map((selectedNode: RowNode) => selectedNode.data.id);
        this.materialSet.technicalCode = this.additionalOptionsForAddColorSetForm.value.technicalCode;
      }
    }
  }

  private getSuggestionForMaterialSetName(): void {
    if (this.canEditMaterialSet() && this.hasCreelPositions()) {
      this.textileDataService
        .generateName(this.materialSet)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((name: string) => (this.suggestedMaterialSetName = name));
    }
  }
}
